import React, {useState} from 'react';

import {Owner, SchemaCollaborator} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import {DefaultSchemaCollaboratorsConfigurationSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AdapterField} from 'instruments/connections/ConnectionConstants';
import {getAdapter} from 'instruments/connections/ConnectionDAO';
import {BaseFieldTableProps, ClientSchemaField, createClientSchemaField} from 'registry/field/FieldTable';
import SchemaWithOrgPermissionsCreateFlow from 'schemas/SchemaWithOrgPermissionsCreateFlow';
import {withLoadingScreen} from 'util/Promises';

import {ConnectionSchemaEditorQuery} from './__generated/ConnectionSchemaEditorQuery';
import {createConnectionSchema, connectionSchemaURL} from './ConnectionSchemaDAO';
import ConnectionSchemaMetadataSettings, {getSpecForRequest} from './ConnectionSchemaMetadataSettings';

type Organization = NonNullable<ConnectionSchemaEditorQuery['currentTenantOrganizations']>[number];

function createSchemaFieldFromAdapterField(adapterField: AdapterField): ClientSchemaField {
  const schemaField = createClientSchemaField(adapterField.displayName, adapterField.id);
  schemaField.isRequired = adapterField.isRequired;
  return schemaField;
}

export interface ConnectionSchemaCreateFlowProps {
  // Human-readable summaries of dependent computed fields, lookup fields, etc
  schemaFieldDependentsSummariesById: BaseFieldTableProps['schemaFieldDependentsSummariesById'];
  ownerChoices: Array<Owner>;
  organizations: Array<Organization>;
}

export default function ConnectionSchemaCreateFlow(props: ConnectionSchemaCreateFlowProps): JSX.Element {
  const [adapterId, setAdapterId] = useState('');
  const [name, setName] = useState('');
  const [ownerId, setOwnerId] = useState(props.ownerChoices[0]?.id);
  const [sqlIdentifier, setSqlIdentifier] = useState<string | null>(null);
  const [idPrefix, setIdPrefix] = useState<string | null>(null);
  const [fields, setFields] = useState<Array<ClientSchemaField>>([]);
  const [adapterFields, setAdapterFields] = useState<Array<ClientSchemaField>>([]);

  function createSchema(collaborators?: Array<SchemaCollaborator>): void {
    const spec = getSpecForRequest([...fields, ...adapterFields], idPrefix || '');
    if (spec) {
      withLoadingScreen(
        (async () => {
          const {schemaId} = await createConnectionSchema({
            ownerId,
            name,
            spec,
            sqlIdentifier:
              BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME && sqlIdentifier ? sqlIdentifier : null,
            collaborators,
            adapterId,
          });
          BenchlingUtil.navigate(null, connectionSchemaURL(schemaId));
        })(),
        'Creating schema...'
      );
    }
  }

  const handleAdapterChange = async (adapterId: string): Promise<void> => {
    setAdapterId(adapterId);
    const adapter = await getAdapter(adapterId);
    setAdapterFields(adapter.fields.map((adapterField) => createSchemaFieldFromAdapterField(adapterField)));
  };

  function renderMetadataSettings(handleNext: () => void): JSX.Element {
    return (
      <ConnectionSchemaMetadataSettings
        adapterId={adapterId}
        setAdapterId={handleAdapterChange}
        name={name}
        setName={setName}
        ownerId={ownerId}
        setOwnerId={setOwnerId}
        sqlIdentifier={sqlIdentifier}
        setSqlIdentifier={setSqlIdentifier}
        idPrefix={idPrefix}
        setIdPrefix={setIdPrefix}
        fields={fields}
        setFields={setFields}
        shouldAllowSchemaUpdates={false}
        schema={null}
        schemaFieldDependentsSummariesById={props.schemaFieldDependentsSummariesById}
        ownerChoices={props.ownerChoices}
        handleNext={handleNext}
      />
    );
  }
  return (
    <SchemaWithOrgPermissionsCreateFlow
      createSchema={createSchema}
      defaultSchemaCollaboratorsSchemaType={DefaultSchemaCollaboratorsConfigurationSchemaType.CONNECTION}
      metadataTitle="Connection Schema Settings"
      isSchemaPermissionsEnabled={true}
      ownerId={ownerId}
      organizations={props.organizations}
      renderMetadataSettings={renderMetadataSettings}
    />
  );
}
